/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import { Close, ExpandMore } from "@mui/icons-material";

import { usePractitionerRoleContext } from "../../../contexts/PractitionerRoleContext";

import { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { GenericTable } from "../../../components/GenericTable/GenericTable";
import { PrescriptionActions } from "./prescription/PrescriptionActions";
import {
  translateDocuments,
  translateTypes,
} from "./prescription/PrescriptionAuxiliarTranslation";

import { CircularLoading, NoDataWarning } from "../../../components/common";
import { useInterventionsContext } from "../../../contexts/InterventionsContext";
import { useMedicalCareContext } from "../../../contexts/MedicalCareContext";
import { DocumentVisualization } from "./DocumentVisualization/DocumentVisualization";
import { SignedDocsActions } from "./prescription/SignedDocsActions";
import { collectSignedDocumentsForEncounter } from "../utils/enrichInterventionsWithSignedDocuments";

export function TabsAssinatura({ patientName, patientCPF }: any) {
  const { userPractitionerRole } = usePractitionerRoleContext();
  const { base64, setBase64, selectedInterventions } =
    useInterventionsContext();
  const { clinicalLocation, summaryData } = useMedicalCareContext();
  const { state } = useLocation();

  const [showPdf, setShowPdf] = useState<boolean>(false);

  const [docToRender, setDocToRender] = useState<string>("");

  const [signedDocView, setSignedDocView] = useState<any>();

  interface InterventionListItem {
    type?: string;
    sign?: unknown;
    name?: string;
    label?: string;
    title?: string;
    description?: string;
    documentReferenceId?: string;
    binaryUrl?: string;
    signedBase64?: string;
    signedDocumentType?: string;
  }

  const columns = [
    { column: "Tipo de documento", rowName: "type" },
    { column: "Assinatura", rowName: "sign" },
  ];

  function translateExams(exams: any) {
    // category pode faltar em itens hidratados na edição (ServiceRequest sem category)
    const categoryCode = exams?.category?.code;
    if (!["108252007", "363679005"].includes(categoryCode)) {
      return "Exames gerais";
    } else if (categoryCode === "108252007") {
      return "Exame laboratorial";
    } else if (categoryCode === "363679005") {
      return "Exame de imagem";
    } else {
      return "-";
    }
  }

  const pendingUnsignedInterventions: InterventionListItem[] =
    (selectedInterventions?.all?.filter(
      (intervention: InterventionListItem) => !intervention?.sign,
    ) as InterventionListItem[]) ?? [];

  const pendingQuestionnaires = pendingUnsignedInterventions.filter(
    (intervention) => intervention?.type === "questionnaire",
  );

  const hasPendingNonQuestionnaire = pendingUnsignedInterventions.some(
    (intervention) => intervention?.type !== "questionnaire",
  );

  const selectedQuestionnaireLabels = pendingQuestionnaires
    .map((questionnaire) => {
      return (
        questionnaire?.name ||
        questionnaire?.label ||
        questionnaire?.title ||
        questionnaire?.description ||
        "Questionário"
      );
    })
    .filter(Boolean);

  // DocumentReference assinados do encounter (Patient Summary) + sessão
  const encounterId = state?.encounterReference?.id;
  const signedDocumentsFromFhir = useMemo(
    () => collectSignedDocumentsForEncounter(summaryData, encounterId),
    [summaryData, encounterId]
  );

  const signedInterventionRows = useMemo(() => {
    const fromSession =
      selectedInterventions?.all
        ?.filter((el: InterventionListItem) => el?.sign)
        ?.map((el: InterventionListItem, index: number) => ({
          type:
            el?.signedDocumentType ||
            translateTypes[el?.type as string] ||
            el?.type,
          description: el?.name || el?.label || "Documento",
          base64: el?.signedBase64,
          binaryUrl: el?.binaryUrl,
          documentReferenceId: el?.documentReferenceId,
          index,
          source: "session" as const,
        })) || [];

    const knownDocIds = new Set(
      fromSession
        .map((r: any) => r.documentReferenceId)
        .filter(Boolean)
    );

    // Docs FHIR do encounter ainda não representados pelas intervenções
    const fromFhirOrphans = signedDocumentsFromFhir
      .filter(
        (d) =>
          d.documentReferenceId && !knownDocIds.has(d.documentReferenceId)
      )
      .map((d, index) => ({
        type: d.type,
        description: d.description,
        base64: undefined as string | undefined,
        binaryUrl: d.binaryUrl,
        documentReferenceId: d.documentReferenceId,
        index: fromSession.length + index,
        source: "fhir" as const,
      }));

    return [...fromSession, ...fromFhirOrphans];
  }, [selectedInterventions?.all, signedDocumentsFromFhir]);

  return (
    <Stack width="100%" maxHeight="70vh" overflow="auto" padding={2}>
      {pendingQuestionnaires.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Stack spacing={1}>
            <Typography variant="body1">
              Os seguintes questionários foram selecionados e serão enviados
              para o paciente ao finalizar o atendimento. O paciente poderá
              acessar e responder ao entrar na plataforma:{" "}
              <strong>
                {selectedQuestionnaireLabels
                  .map(
                    (questionnaireLabel, questionnaireIndex) =>
                      questionnaireLabel,
                  )
                  ?.join(", ")}
              </strong>
            </Typography>
          </Stack>
        </Alert>
      )}
      <Accordion defaultExpanded sx={{ width: "100%" }} elevation={5}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <Stack direction="row" alignItems="center" gap={1}>
            Assinaturas pendentes
            <Avatar
              sx={{
                width: 26,
                height: 26,
                fontSize: "14px",
                fontWeight: 600,
                backgroundColor: "primary100.main",
                color: "primary1000.main",
              }}
            >
              {base64?.filter((e: any) => !e?.[Object.keys(e)?.[0]]?.sign)
                ?.length || "0"}
            </Avatar>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          {!base64 && hasPendingNonQuestionnaire ? (
            <CircularLoading />
          ) : (
            <Grid container mt={2} borderRadius={1}>
              <Grid item md={showPdf ? 6 : 12} xs={12}>
                {pendingUnsignedInterventions?.length ? (
                  <>
                    <GenericTable
                      tableRows={
                        base64
                          ? base64
                              ?.map((el: any, index: number) => {
                                const findKey = el?.[`${Object.keys(el)?.[0]}`];
                                const listIndex = findKey?.info?.map((e: any) =>
                                  selectedInterventions?.all.indexOf(e),
                                );

                                return {
                                  type: findKey?.type.includes("exam")
                                    ? translateExams(findKey?.info?.[0])
                                    : translateDocuments[
                                        findKey?.type?.split("-")?.[0]
                                      ] ||
                                      translateDocuments[
                                        findKey?.type?.split("-")?.[1]
                                      ] ||
                                      "",
                                  sign: findKey?.sign
                                    ? "Realizada"
                                    : "Pendente",
                                  base64: findKey?.base64,
                                  index,
                                  uuid: findKey?.uuid,
                                  idValue: findKey?.idValue,
                                  listIndex,
                                  key: Object.keys(el)?.[0],
                                };
                              })
                              ?.filter((e: any) => e.sign === "Pendente")
                          : []
                      }
                      tableColumns={columns}
                      actions={(rowData: any) => (
                        <PrescriptionActions
                          row={rowData}
                          setShowPdf={setShowPdf}
                          setDocToRender={setDocToRender}
                        />
                      )}
                    />
                  </>
                ) : (
                  <>
                    <NoDataWarning />
                  </>
                )}
              </Grid>
              <Grid
                display={showPdf ? "flex" : "none"}
                justifyContent="center"
                item
                md={6}
                xs={12}
              >
                {showPdf && (
                  <Stack justifyContent="center" alignItems="center">
                    <Stack width="100%">
                      <Tooltip title="Fechar">
                        <IconButton
                          sx={{ alignSelf: "flex-end" }}
                          onClick={() => setShowPdf(false)}
                        >
                          <Close />
                        </IconButton>
                      </Tooltip>

                      <Stack
                        width="100%"
                        minWidth={{ xs: "200px", md: "500px", lg: "800px" }}
                        minHeight="500px"
                      >
                        <iframe
                          src={`${docToRender}#zoom=80`}
                          title="Atendimento"
                          width="100%"
                          height="100%"
                        />
                      </Stack>
                    </Stack>
                  </Stack>
                )}
              </Grid>
            </Grid>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ width: "100%" }} elevation={5}>
        <AccordionSummary
          expandIcon={<ExpandMore />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <Stack direction="row" alignItems="center" gap={1}>
            Registros assinados
            <Avatar
              sx={{
                width: 26,
                height: 26,
                fontSize: "14px",
                fontWeight: 600,
                backgroundColor: "primary100.main",
                color: "primary1000.main",
              }}
            >
              {signedInterventionRows?.length || "0"}
            </Avatar>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container mt={2} borderRadius={1}>
            <Grid item md={signedDocView ? 6 : 12} xs={12}>
              {signedInterventionRows?.length ? (
                <GenericTable
                  tableRows={signedInterventionRows}
                  tableColumns={[
                    { column: "Descrição", rowName: "description" },
                    { column: "Tipo de documento", rowName: "type" },
                  ]}
                  actions={(rowData: any) => (
                    <SignedDocsActions
                      row={rowData}
                      setSignedDocView={setSignedDocView}
                    />
                  )}
                />
              ) : (
                <>
                  <NoDataWarning />
                </>
              )}
            </Grid>
            <Grid
              display={signedDocView ? "flex" : "none"}
              justifyContent="center"
              item
              md={6}
              xs={12}
            >
              {signedDocView && (
                <Stack justifyContent="center" alignItems="center">
                  <Stack width="100%">
                    <Tooltip title="Fechar">
                      <IconButton
                        sx={{ alignSelf: "flex-end" }}
                        onClick={() => setSignedDocView(undefined)}
                      >
                        <Close />
                      </IconButton>
                    </Tooltip>

                    <Stack
                      width="100%"
                      minWidth={{ xs: "200px", md: "500px", lg: "800px" }}
                      minHeight="500px"
                    >
                      <iframe
                        src={`data:application/pdf;base64,${signedDocView}#zoom=80`}
                        title="Atendimento"
                        width="100%"
                        height="100%"
                      />
                    </Stack>
                  </Stack>
                </Stack>
              )}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      {/* Lista que renderiza os documentos para gerar o base64 */}

      <div style={{ display: "none" }}>
        <DocumentVisualization
          setBase64={setBase64}
          dataPdf={[
            {
              patientCPF: patientCPF,
              patientData: patientName,
              professionalData: userPractitionerRole,
              location: clinicalLocation,
            },
          ]}
        />
      </div>
    </Stack>
  );
}
